import {
  computed,
  defineComponent,
  PropType,
  reactive,
  ref,
  shallowRef
} from '@vue/composition-api';

import { downloadAssetFromUrl } from '../../composables/utils.composable';
import AssetViewer from '../asset-viewer/asset-viewer.component.vue';
import { IAssetListItem } from './asset-list.types';

export default defineComponent({
  components: {
    AssetViewer
  },
  emits: ['remove'],
  inheritAttrs: false,
  name: 'AssetList',
  props: {
    isEditMode: {
      default: false,
      type: Boolean
    },
    items: {
      default: () => [],
      type: Array as PropType<IAssetListItem[]>
    },
    listType: {
      default: 'grid',
      type: String as PropType<'grid' | 'list'>
    },
    showDownload: {
      default: true,
      type: Boolean
    },
    size: {
      default: 148,
      type: Number
    }
  },
  setup(props, { emit }) {
    const previewAssetUid = ref<number | null>(null);
    const isPreviewAssetVisible = ref<boolean>(false);
    const itemsInError = reactive<number[]>([]);

    const assetActionRef = shallowRef<HTMLDivElement[]>();

    const itemsViewer = computed(
      () =>
        props.items?.filter((item) => !itemsInError.includes(item.uid)) || []
    );

    const isImage = (asset: IAssetListItem) => {
      const { contentType } = asset || {};

      return contentType?.startsWith('image/') ?? false;
    };

    const isVideo = (asset: IAssetListItem) => {
      const { contentType } = asset || {};

      return contentType?.startsWith('video/') ?? false;
    };

    const handleSrc = (asset: IAssetListItem) => {
      let src: string | undefined = undefined;

      const { content, contentType, url } = asset || {};

      if (url) {
        src = url;
      } else if (content && contentType) {
        src = `data:${contentType};base64,${content}`;
      }

      return src;
    };

    const handleDownload = (asset: IAssetListItem) => {
      const src = handleSrc(asset);

      if (src) {
        const { name } = asset || {};

        downloadAssetFromUrl(src, name);
      }
    };

    const handlePreview = (asset: IAssetListItem) => {
      const { uid } = asset || {};

      previewAssetUid.value = uid;

      isPreviewAssetVisible.value = true;
    };

    const handleRemove = (asset: IAssetListItem) => {
      emit('remove', asset);
    };

    const handleAction = (
      action: 'download' | 'preview' | 'remove',
      asset: IAssetListItem,
      index: number
    ) => {
      if (
        (assetActionRef.value &&
          assetActionRef.value[index] &&
          window
            .getComputedStyle(assetActionRef.value[index])
            .getPropertyValue('opacity') === '1') ||
        props.listType === 'list'
      ) {
        switch (action) {
          case 'download':
            handleDownload(asset);
            break;
          case 'preview':
            handlePreview(asset);
            break;
          case 'remove':
            handleRemove(asset);
            break;
        }
      }
    };

    return {
      assetActionRef,
      handleAction,
      handleSrc,
      isImage,
      isPreviewAssetVisible,
      isVideo,
      itemsInError,
      itemsViewer,
      previewAssetUid
    };
  }
});
